<template lang="html">
  <div>
    <!-- Dialog: Preview -->
    <v-dialog
      max-width="1000px"
      v-model="previewDialogOpened"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card>
        <v-card-title class="title">
          <v-layout v-if="renameFile" align-center>
            <span class="caption mr-2">New Name:</span>
            <v-text-field
              v-model="newName"
              hide-details
              solo
              class="default-input"
            />
            <span class="subheading">.{{ documentExtension }}</span>
            <v-tooltip bottom class="mx-2">
              <i
                class="material-icons-outlined success--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameItem()"
                slot="activator"
              >
                check
              </i>
              <span>Save</span>
            </v-tooltip>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined error--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameFile = false"
                slot="activator"
              >
                cancel
              </i>
              <span>Cancel</span>
            </v-tooltip>
          </v-layout>
          <v-layout v-else>
            <span>{{ getDocumentName(selectedDocumentLink) }}</span>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 20px; cursor: pointer"
                @click="startRenaming()"
                slot="activator"
              >
                edit
              </i>
              <span>Rename</span>
            </v-tooltip>
          </v-layout>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1" v-if="!isFirmDocumentsDisabled">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="openCopyDialog()"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                drive_file_move
              </i>
            </v-btn>
            <span>Move File</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="
                downloadDocumentByLink(
                  documentPreviewData,
                  getDocumentName(selectedDocumentLink),
                )
              "
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                get_app
              </i>
            </v-btn>
            <span>Download to Computer</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Add Link -->
    <v-dialog
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="addLinkDialog = false"
      v-model="addLinkDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="title"> Add Link </v-card-title>
        <v-card-text>
          <v-layout column class="text-xs-left">
            <v-flex class="mb-4">
              <div>Link text</div>
              <v-text-field
                class="default-input body-1"
                solo
                hide-details
                v-model="linkText"
                placeholder="e.g. Click this link"
              />
            </v-flex>
            <v-flex>
              <div>Link URL</div>
              <v-text-field
                class="default-input body-1"
                solo
                hide-details
                v-model="linkURL"
                placeholder="e.g. www.google.com"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            class="default-input primary text-capitalize"
            @click="addLink()"
            :disabled="!linkAddable"
          >
            Add
          </v-btn>
          <v-btn
            outline
            class="default-input error--text text-capitalize"
            @click="addLinkDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Copy Item -->
    <v-dialog
      width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="copyItemDialogOpened"
      persistent
      @keydown.esc="copyItemDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title"> Specify new location </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading" wrap>
            <div class="mr-1">Save to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
          </v-layout>
          <v-layout align-center>
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(foldersInLastPath) ? 'No folders' : 'Choose folder'
              "
              hide-details
              :items="foldersInLastPath"
              :disabled="isEmpty(foldersInLastPath)"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="copyItem()"
            :disabled="lastPathInOrder === 'Attachments'"
          >
            Move
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="copyItemDialogOpened = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Firm User -->
    <v-dialog
      v-model="firmUserDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
      persistent
      @keydown.esc="firmUserDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Firm Users in Conversation
        </v-card-title>
        <v-card-text v-if="!isEmpty(firmUsers) && selectedConversation">
          <v-alert outline :value="true" type="info" class="mb-4"
            >Users you select will be notified of every new message in this
            conversation.</v-alert
          >
          <v-flex class="text-xs-left ml-2">
            <div>Team Members</div>
            <v-autocomplete
              multiple
              class="default-input body-1"
              hide-details
              solo
              :items="firmUsers"
              v-model="selectedFirmUserIds"
              item-text="name"
              item-value="id"
            >
              <template slot="selection" slot-scope="data">
                <v-chip
                  small
                  v-if="selectedFirmUserIds.indexOf(data.item.id) === 0"
                >
                  <span>{{ data.item.name }}</span>
                </v-chip>
                <span
                  v-if="selectedFirmUserIds.indexOf(data.item.id) === 1"
                  class="grey--text caption"
                  >(+{{ selectedFirmUserIds.length - 1 }} other{{
                    selectedFirmUserIds.length > 2 ? 's' : ''
                  }})</span
                >
              </template>
            </v-autocomplete>
          </v-flex>
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="100"
            color="primary"
          ></v-progress-circular>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            class="default-input primary text-capitalize"
            @click="updateConversation()"
          >
            Save
          </v-btn>
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="firmUserDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Export Conversations -->
    <ExportConversationDialog
      v-model="showExportDialog"
      v-if="selectedConversation"
      :conversation-id="selectedConversation.id"
      :selected-users="selectedConversationUsers"
    />

    <!-- Partial: Container -->
    <section
      class="conversation-box__container"
      :class="selectedThreadId && 'conversation-box__container--threads'"
    >
      <!-- Partial: Threads aside -->
      <aside v-if="selectedThreadId" class="conversation-box__threads">
        <header class="conversation-box__threads__header">
          <sc-flex :is-y-centered="true" :gap="8">
            <sc-h4>Thread</sc-h4>
            <sc-button-group>
              <sc-button
                @click="showExportDialog = true"
                type="secondary"
                size="small"
                icon-left="download"
                :is-icon-only="true"
                >Download</sc-button
              >
            </sc-button-group>
          </sc-flex>
          <sc-button-group>
            <sc-button
              @click="closeThread()"
              size="small"
              type="secondary"
              icon-left="close"
              :is-icon-only="true"
              >Close</sc-button
            >
          </sc-button-group>
        </header>
        <section class="conversation-box__threads__container">
          <sc-flex
            v-if="selectedThreadLoading"
            :is-centered="true"
            :padding="16"
          >
            <v-progress-circular
              indeterminate
              size="24"
              color="primary"
            ></v-progress-circular>
          </sc-flex>
          <div
            v-else
            class="conversation-box__threads__content"
            ref="threadContainer"
          >
            <!-- Partial: Selected -->
            <SxChatBubble
              v-if="selectedThreadMessage"
              :key="i"
              :current-user="currentUser"
              :preview-document="previewDocument"
              :open-thread="openThread"
              :selected-message-id="selectedThreadId"
              :is-reply="true"
              :message="selectedThreadMessage"
            ></SxChatBubble>

            <!-- Partial: Bubbles -->
            <SxChatBubble
              v-for="(message, i) in selectedThreadMessages"
              :key="i"
              :current-user="currentUser"
              :preview-document="previewDocument"
              :open-thread="openThread"
              :is-reply="true"
              :message="message"
            ></SxChatBubble>
          </div>
          <footer class="conversation-box__threads__footer">
            <form>
              <sc-button-group>
                <sc-button
                  @click="sendReply()"
                  size="small"
                  :is-disabled="sendingReply"
                  :is-loading="sendingReply"
                  type="primary"
                  icon-right="send"
                  >Send</sc-button
                >
              </sc-button-group>
              <textarea
                placeholder="Message"
                v-model="newReply"
                class="reply-textarea"
                :class="sendingReply && 'reply-textarea--disabled'"
              ></textarea>
            </form>
          </footer>
        </section>
      </aside>

      <!-- Partial: Header -->
      <header class="conversation-box__header">
        <sc-h4>
          <span v-if="selectedConversation && selectedConversation.name">{{
            selectedConversation.name
          }}</span
          ><span v-else>{{ selectedConversationUsers }}</span>
        </sc-h4>
        <v-spacer />

        <v-select
          v-if="!isEmpty(conversationCompanies)"
          class="hidden-xs-only default-input"
          :items="conversationCompaniesMapped"
          :placeholder="
            conversationCompanies.length +
              ' related entit' +
              (conversationCompanies.length > 1 ? 'ies' : 'y')
          "
          solo
          :value="selectedEntityId"
          hide-details
          item-text="name"
          item-value="id"
          clearable
          @change="changeCompany"
          style="max-width: 200px;"
        />
        <div v-else />

        <sc-button-group>
          <sc-button
            @click="showExportDialog = true"
            size="small"
            icon-left="download"
            type="secondary"
            :is-icon-only="true"
            :is-disabled="!hasConversationMessage"
            :with-tooltip="true"
            >Export Conversation</sc-button
          >
          <sc-button
            @click="markAsUnseen()"
            icon-left="eye"
            size="small"
            type="secondary"
            :is-disabled="!hasConversationMessage"
            :is-icon-only="true"
            :with-tooltip="true"
            >Mark as unseen</sc-button
          >
          <sc-button
            v-if="hasReminders"
            @click="removeReminder()"
            size="small"
            icon-left="calendar"
            type="secondary"
            :is-icon-only="true"
            :with-tooltip="true"
          >
            Remove reminder for
            {{
              moment(
                selectedConversation.reminders.find(
                  r => r.user_id === currentUser.id,
                ).scheduled_time,
              ).format('lll')
            }}
          </sc-button>
          <sc-button
            @click="openFirmUserDialog()"
            size="small"
            icon-left="gear"
            type="secondary"
            :is-icon-only="true"
            >Manage Firm Users</sc-button
          >
        </sc-button-group>
      </header>

      <sc-hr :is-light="true"></sc-hr>

      <!-- Partial: Chat Content -->
      <div
        class="conversation-box__content"
        ref="messageContainer"
        :class="!isScrolling ? 'conversation-box__content--smooth' : ''"
      >
        <!-- Note: No relevant company ID -->
        <sc-alert
          v-if="
            selectedConversation &&
              isEmpty(selectedConversation.relevant_company_ids) &&
              !isLoading
          "
          type="note"
        >
          There are no related entities for this conversation. Please use a
          different conversation as messaging is disabled for this conversation
          or message us if you want the message history to be moved to a
          different conversation.
        </sc-alert>

        <!-- Note: Current user is not in this conversation -->
        <sc-alert
          v-else-if="
            selectedConversation &&
              !selectedConversation.advisor_ids.includes(currentUser.id)
          "
          type="note"
        >
          You are not in this conversation. Send a message below or add yourself
          in the notifications by clicking the <b>Firm Users</b> button on the
          right.
        </sc-alert>

        <!-- Note: No messages -->
        <sc-alert
          v-else-if="selectedConversation && isEmpty(selectedMessages)"
          type="note"
        >
          There are no messages in this conversation.
          {{
            selectedConversation.has_test_clients
              ? 'The test client will reply immediately in this conversation.'
              : ''
          }}
        </sc-alert>

        <!-- Alert: Conversation with self -->
        <sc-alert v-else-if="isSelfSelected" type="danger">
          You cannot have a conversation with yourself.
        </sc-alert>

        <!-- Partial: Loading -->
        <sc-flex v-if="loading" :is-column="true" :is-centered="true">
          <v-progress-circular
            indeterminate
            size="32"
            color="primary"
          ></v-progress-circular>
        </sc-flex>

        <!-- Partial: Content -->
        <section
          v-else-if="selectedConversation"
          class="conversation-box__bubble-container"
          :class="
            isScrolling ? 'conversation-box__bubble-container--scrolling' : ''
          "
        >
          <!-- Partial: Bubble(s) -->
          <SxChatBubble
            v-for="(message, i) in selectedMessages"
            :key="i"
            :current-user="currentUser"
            :preview-document="previewDocument"
            :open-thread="openThread"
            :message="message"
          ></SxChatBubble>

          <!-- Partial: Last message marker -->
          <div style="display: none" ref="lastMessage" />
          <v-layout column align-center>
            <v-btn
              small
              flat
              class="white primary text-capitalize ma-0"
              slot="activator"
              v-if="unseenThreadId && !selectedThreadLoading"
              @click="openThread(unseenThreadId)"
            >
              Go to unseen thread
            </v-btn>
          </v-layout>

          <!-- Partial: New Bubbles? -->
          <template
            v-if="
              lastMessageInSelectedConversation &&
                !lastMessageInSelectedConversation.scheduled
            "
          >
            <div
              class="text-xs-right grey--text text--darken-1 mr-1"
              v-if="
                lastMessageInSelectedConversation &&
                  lastMessageInSelectedConversation.unseen_user_ids.length >=
                    1 &&
                  lastMessageInSelectedConversation.user_id === currentUser.id
              "
            >
              Unseen by
              {{ lastMessageInSelectedConversation.unseen_user_ids.length }}
            </div>
            <div
              class="text-xs-right grey--text text--darken-1 mr-1"
              v-else-if="
                lastMessageInSelectedConversation &&
                  lastMessageInSelectedConversation.unseen_user_ids.length ===
                    0 &&
                  lastMessageInSelectedConversation.user_id === currentUser.id
              "
            >
              Seen by All
            </div>
          </template>
        </section>
      </div>

      <!-- Partial: Message Editor -->
      <MessageEditorNew
        v-if="shouldShowEditor"
        :current-user="currentUser"
        :conversation-companies="conversationCompanies"
        :selected-conversation-id="selectedConversationId"
        @messageSent="refreshConversation"
      />
    </section>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { supportedExtensionsForPreview } from '@/constants/variables';
import API from '@/api';
import ExportConversationDialog from './dialogs/ExportConversationDialog';
import firebase from 'firebase';
import get from 'lodash/get';
import heicConvert from 'heic-convert';
import isEmpty from 'lodash/isEmpty';
import MessageEditorNew from '@/components/MessageEditorNew';
import moment from 'moment';
import PreviewDocument from '@/components/PreviewDocument';
import SxChatBubble from '@/components/sx/SxChatBubble';

export default {
  name: 'ConversationBoxNew',
  props: {
    selectedConversationId: {
      required: false,
      type: Number,
      default: undefined,
    },
    currentUser: {
      required: true,
      type: Object,
    },
    lastUnreadMessageId: {
      required: false,
      type: Number,
      default: null,
    },
    hashCompany: {
      required: false,
      type: Object,
      default: () => {},
    },
  },
  components: {
    ExportConversationDialog,
    PreviewDocument,
    MessageEditorNew,
    SxChatBubble,
  },
  data() {
    return {
      addLinkDialog: false,
      cancelingMessage: false,
      copyItemDialogOpened: false,
      documentPreviewData: '',
      firmUserDialogOpened: false,
      firmUsers: [],
      foldersInLastPath: [],
      isScrolling: true,
      lastPathInOrder: '',
      linkText: '',
      linkURL: '',
      listeningToNewMessages: null,
      loading: false,
      messageTag: 'general',
      newMessagesRef: null,
      newName: '',
      newReply: '',
      pathsInOrder: [],
      previewDialogOpened: false,
      previewLoaded: false,
      previewRotation: 0,
      previewType: '',
      remindClient: false,
      reminderDateRules: [
        v => moment() < moment(v) || !v || 'Due date must be in the future',
      ],
      reminderSaving: false,
      renameFile: false,
      selectedCompanyId: null,
      selectedConversation: null,
      selectedDocumentLink: '',
      selectedDocumentName: '',
      selectedEntityId: null,
      selectedFirmUserIds: [],
      selectedMessageId: null,
      selectedThreadId: null,
      selectedThreadMessages: [],
      selectedThreadLoading: false,
      sending: false,
      sendingReply: false,
      showExportDialog: false,
      supportedExtensionsForPreview,
    };
  },
  computed: {
    ...mapGetters([
      'savedConversationPageMessage',
      'savedConversationDialogMessage',
      'isFirmDocumentsDisabled',
      'currentFirm',
    ]),
    ...mapState('companies', ['companies']),
    selectedThreadMessage() {
      if (!this.selectedConversation) return null;

      const allMessages = this.selectedConversation.messages || [];
      const selectedThreadMessage = allMessages.find(
        m => m.id === this.selectedThreadId,
      );

      return selectedThreadMessage;
    },
    selectedMessages() {
      const allMessages = this.selectedConversation.messages || [];
      const filteredMessages = allMessages
        .map(message => {
          message.threadMessages = [];

          return message;
        })
        .filter(message => !!message && !message.message_thread_id);

      allMessages.forEach(message => {
        const threadMessages = allMessages.filter(
          m => message.id === m.message_thread_id,
        );

        message.threadMessages = threadMessages || [];
      });

      return filteredMessages;
    },
    unseenThreadId() {
      if (!this.selectedConversation) return false;

      const unseenThread = this.selectedMessages.find(m =>
        m.threadMessages.find(
          tm =>
            tm.unseen_user_ids &&
            tm.unseen_user_ids.includes(this.currentUser.id),
        ),
      );
      if (unseenThread && unseenThread.id !== this.selectedThreadId) {
        return unseenThread.id;
      } else {
        return false;
      }
    },
    hasConversationMessage() {
      return this.selectedConversation && !isEmpty(this.selectedMessages);
    },
    shouldShowEditor() {
      return (
        this.selectedConversation &&
        !isEmpty(this.selectedConversation.relevant_company_ids)
      );
    },

    lastMessageInSelectedConversation() {
      const lastIndex = this.selectedMessages.length - 1;

      return this.selectedMessages[lastIndex];
    },
    conversationCompanies() {
      if (
        this.selectedConversation &&
        this.selectedConversation.relevant_company_ids
      ) {
        return this.companies.filter(c =>
          this.selectedConversation.relevant_company_ids.includes(c.id),
        );
      }
    },
    linkAddable() {
      return !isEmpty(this.linkText) && !isEmpty(this.linkURL);
    },
    selectedConversationUsers() {
      if (this.selectedConversation) {
        if (!isEmpty(this.selectedConversation.client_names)) {
          return this.selectedConversation.client_names
            .filter(u => u !== this.currentUser.full_name)
            .join(', ');
        } else {
          return this.selectedConversation.advisor_names
            .filter(u => u !== this.currentUser.full_name)
            .join(', ');
        }
      }
    },
    hasReminders() {
      if (this.selectedConversation) {
        return !isEmpty(
          this.selectedConversation.reminders.filter(
            r => r.user_id === this.currentUser.id,
          ),
        );
      }
    },
    conversationCompaniesMapped() {
      if (this.conversationCompanies) {
        return this.conversationCompanies.map(c => {
          return {
            id: c.id,
            name: `${c.tax_return_name} (${c.entity_type})`,
          };
        });
      }
    },
    isSelfSelected() {
      const isSelfSelected = Boolean(
        this.selectedConversation &&
          this.selectedConversation.user_ids &&
          this.selectedConversation.user_ids.length &&
          this.selectedConversation.user_ids.length === 1 &&
          this.selectedConversation.user_ids[0] === this.currentUser.id,
      );

      return isSelfSelected;
    },
  },
  methods: {
    moment,
    get,
    heicConvert,
    changeCompany(id) {
      if (id) {
        this.selectedEntityId = null;
        this.$emit('change-company', id);
      }
    },
    async sendReply() {
      if (!this.newReply) return;

      this.sendingReply = true;

      try {
        await API.conversation.sendMessage({
          text: this.newReply
            .trim()
            .split('\n')
            .join('<br/>'),
          conversationId: this.selectedConversationId,
          messageThreadId: this.selectedThreadId,
        });

        const newMessage = {
          id: null,
          user_id: this.currentUser.id,
          user_name: this.currentUser.full_name,
          text: this.newReply,
          avatar_url: this.currentUser.avatar,
          created_at: moment(),
        };

        this.selectedThreadMessages = [
          ...this.selectedThreadMessages,
          newMessage,
        ];
      } catch (error) {
        console.error(error);
      } finally {
        this.newReply = '';
        this.sendingReply = false;
        this.scrollThreadsToBottom();
      }
    },
    scrollThreadsToBottom() {
      setTimeout(() => {
        const scrollElement = this.$refs.threadContainer;

        if (!scrollElement) return;

        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 100);
    },
    async loadThreadMessages(isSilent = false) {
      if (!this.selectedThreadId) return void 0;

      try {
        this.selectedThreadLoading = !isSilent;

        const { data } = await API.conversation.getThreadMessages(
          this.selectedConversationId,
          this.selectedThreadId,
        );

        this.selectedThreadMessages = data.messages;
      } catch (error) {
        console.error(error);
      } finally {
        this.selectedThreadLoading = false;
        this.scrollThreadsToBottom();
        API.conversation.updateThreadAsSeen({
          conversationId: this.selectedConversationId,
          threadId: this.selectedThreadId,
        });
      }
    },
    openThread(messageId) {
      this.selectedThreadId = messageId;
    },
    async closeThread() {
      this.selectedThreadId = null;
      this.selectedThreadLoading = true;
      this.selectedThreadMessages = [];
      await this.refreshConversation();
      this.scrollToBottom();
      this.selectedThreadLoading = false;
    },
    async updateConversation() {
      if (isEmpty(this.selectedFirmUserIds)) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'You have to have at least one team member in the conversation.',
        });
        this.firmUserDialogOpened = false;
      } else {
        try {
          const response = await API.conversation.updateConversation({
            id: this.selectedConversation.id,
            advisor_ids: this.selectedFirmUserIds,
          });
          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem saving the conversation.',
            });
          } else {
            this.$store.dispatch('application/setSuccessNotification', {
              message: 'Conversation was saved.',
            });
            this.selectedConversation.advisor_ids = this.selectedFirmUserIds;
            this.firmUserDialogOpened = false;
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    async removeAutoReminder() {
      try {
        const response = await API.conversation.removeAutoReminder(
          this.selectedConversation.id,
        );
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem removing the auto reminder.',
          });
          console.error(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've removed the auto reminder.",
          });
          this.selectedConversation = response.data;
        }
      } catch (error) {
        console.error(error);
      }
    },
    addLink() {
      if (this.linkURL.includes('https:')) {
        this.newMessage = this.newMessage += `<a href="${this.linkURL}">${this.linkText}</a>`;
      } else {
        this.newMessage = this.newMessage += `<a href="https://${this.linkURL}">${this.linkText}</a>`;
      }
      this.addLinkDialog = false;
      this.linkURL = '';
      this.linkText = '';
    },
    startRenaming() {
      this.documentExtension = this.getDocumentName(this.selectedDocumentLink)
        .split('.')
        .pop();
      this.newName = this.getDocumentName(this.selectedDocumentLink).replace(
        `.${this.documentExtension}`,
        '',
      );
      this.renameFile = true;
    },
    async markAsUnseen() {
      this.$emit('markAsUnseen', true);
    },
    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.target = '_blank';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(suffix => string.toLowerCase().endsWith(suffix));
    },
    async previewDocument(documentLink, message) {
      this.selectedMessageId = message.id;
      this.previewLoaded = false;
      try {
        const response = await API.conversation.downloadAttachment(
          documentLink,
        );
        if (response.data.document) {
          this.previewDialogOpened = true;
          console.log(documentLink);
          let company = this.companies.find(
            c => c.name === documentLink.split('/')[2],
          );
          this.selectedCompanyId = !isEmpty(company) ? company.id : null;

          this.selectedDocumentLink = documentLink;
          this.previewType = this.endsWithAny(
            this.supportedExtensionsForPreview,
            this.getDocumentName(documentLink),
          );
          if (this.previewType === 'heic') {
            this.documentPreviewData = await this.convertToJPEG(
              response.data.document.data.link,
            );
          } else {
            this.documentPreviewData = response.data.document.data.link;
          }
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.previewLoaded = true;
      }
    },
    async convertToJPEG(url) {
      const output = await fetch(url).then(async data => {
        const buffer = Buffer.from(await data.arrayBuffer());
        return heicConvert({ buffer, format: 'JPEG' });
      });
      const imgBase64 = btoa(
        output.reduce(
          (data, byte) => `${data}${String.fromCharCode(byte)}`,
          '',
        ),
      );
      return `data:image/png;base64,${imgBase64}`;
    },
    goBackPathForMove(path, index) {
      if (index !== this.pathsInOrder.length - 1) {
        this.pathsInOrder = this.pathsInOrder.slice(0, index + 1);
        this.getFoldersInLastPath();
        this.lastPathInOrder = null;
      }
    },
    changePath() {
      this.pathsInOrder.push(this.lastPathInOrder);
      this.getFoldersInLastPath();
      this.lastPathInOrder = null;
    },
    openCopyDialog() {
      this.pathsInOrder = this.selectedDocumentLink.split('/');
      this.pathsInOrder.shift();
      this.pathsInOrder.pop();
      this.pathsInOrder.pop();
      this.lastPathInOrder = null;
      this.getFoldersInLastPath();
      this.copyItemDialogOpened = true;
    },
    async getFoldersInLastPath() {
      try {
        const response = await API.conversation.getFolders(
          '/'.concat(this.pathsInOrder.join('/')),
        );
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem retrieving folders`,
          });
        } else {
          this.foldersInLastPath = response.data;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async openFirmUserDialog() {
      const response = await API.firm.getAllUsers();
      if (response.data.error) {
        console.log(response.data.error);
      } else {
        this.firmUsers = response.data.users;
        this.selectedFirmUserIds = this.selectedConversation.advisor_ids;
      }
      this.firmUserDialogOpened = true;
    },
    async copyItem() {
      try {
        this.copyItemDialogOpened = false;
        this.copying = true;
        await API.conversation.copyItem({
          link: this.selectedDocumentLink,
          newPath: this.pathsInOrder.join('/'),
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully moved the file!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem moving the file.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.copying = false;
        this.pathsInOrder = [];
      }
    },
    async renameItem() {
      try {
        this.sending = true;
        const response1 = await API.conversation.renameItem({
          newName: this.newName.concat('.', this.documentExtension),
          messageId: this.selectedMessageId,
          link: this.selectedDocumentLink,
        });
        if (response1.data.error) {
          console.log(response1.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.selectedDocumentLink = response1.data;
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You successfully renamed the document!`,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem renaming the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        await this.refreshConversation();
        this.newName = '';
        this.renameFile = false;
        this.documentExtension = '';
        this.sending = false;
      }
    },
    isEmpty,
    async refreshConversation() {
      if (!this.selectedConversationId) return;

      try {
        const response = await API.conversation.getConversation(
          this.selectedConversationId,
        );

        this.selectedConversation = Object.assign({}, response.data);
        await this.loadThreadMessages(true);
      } catch (error) {
        console.error(error);
        this.$emit('closeConversationDialog');
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Not found.',
        });
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },
    subscribeToNewMessages() {
      const database = firebase.database();

      this.newMessagesRef = database.ref(
        `users/${this.currentUser.id}/newMessages`,
      );
      this.listeningToNewMessages = this.newMessagesRef.on(
        'value',
        async () => {
          if (this.currentUser.id) {
            await this.refreshConversation();
          }
        },
      );
    },
    scrollToBottom() {
      const scrollDelay = this.isScrolling ? 240 : 0;

      setTimeout(() => {
        const scrollElement = this.$refs.messageContainer;

        if (!scrollElement) return;
        if (!this.selectedThreadId) this.setScrollPosition();

        // NOTE @chris This is necessary to make only non-initial scrolls smooth and to make sure things are mounted.
        setTimeout(() => {
          this.isScrolling = false;
        }, 100);
      }, scrollDelay);
    },
    setScrollPosition() {
      const scrollElement = this.$refs.messageContainer;

      if (!scrollElement) return;

      scrollElement.scrollTop = scrollElement.scrollHeight;
      API.conversation.updateConversationAsSeen(this.selectedConversationId);
      this.$store.dispatch(
        'updateConversationAsSeen',
        this.selectedConversationId,
      );
    },
    unsubscribeToNewMessages() {
      if (this.listeningToNewMessages && this.newMessagesRef) {
        this.newMessagesRef.off('value', this.listeningToNewMessages);
      }
    },
    async reload() {
      this.loading = true;

      if (
        !isEmpty(this.savedConversationPageMessage) ||
        !isEmpty(this.savedConversationDialogMessage)
      ) {
        this.newMessage = this.savedConversationPageMessage;
        this.$store.dispatch('setSavedConversationPageMessage', '');
      }

      this.selectedEntityId = null;

      await this.refreshConversation();
      this.closeThread();

      this.openLastUnread(this.lastUnreadMessageId);
    },
    openLastUnread(lastUnreadMessageId) {
      const lastUnreadMessage = this.selectedConversation.messages.find(
        m => m.id === lastUnreadMessageId,
      );
      const isLastMessageThread =
        lastUnreadMessage && lastUnreadMessage.message_thread_id;
      if (isLastMessageThread) {
        this.openThread(lastUnreadMessage.message_thread_id);
        this.setScrollPosition();
      }
    },
  },
  beforeDestroy() {
    this.$store.dispatch('setSavedConversationPageMessage', this.newMessage);
    this.unsubscribeToNewMessages();
  },
  watch: {
    selectedConversationId: {
      async handler() {
        this.reload();
        this.isScrolling = true;
      },
    },
    selectedThreadId: {
      async handler() {
        this.loadThreadMessages();
      },
    },
  },
  async created() {
    this.reload();
    this.subscribeToNewMessages();
  },
};
</script>

<style lang="css" scoped="true">
.conversation-box__container {
  position: relative;

  display: flex;
  flex-direction: column;
  height: 100%;
}

.conversation-box__container--threads {
  /* NOTE @chris This is a future concern when we have a design for a more responsive left panel. */
  /* padding-right: 360px; */
}

.conversation-box__header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  justify-content: space-between;
  padding: 12px 20px;
}

.conversation-box__content {
  flex-grow: 1;
  padding: 16px;

  max-height: calc(100vh - 362px);
  overflow-y: scroll;
}

.conversation-box__content--smooth {
  scroll-behavior: smooth;
}

.conversation-box__bubble-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.conversation-box__threads {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  height: 100%;
  width: 360px;

  background-color: var(--gray-0);
}

.conversation-box__threads__header {
  align-items: center;
  display: flex;
  height: 56px;
  justify-content: space-between;
  padding: 0 16px;

  border-bottom: 1px solid var(--gray-100);
}

.conversation-box__threads__container {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: calc(100% - 56px);

  border-left: 1px solid var(--gray-100);
}

.conversation-box__threads__content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 12px;
  overflow: auto;
  padding: 12px;
}

.conversation-box__threads__footer {
  align-items: end;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;

  border-top: 1px solid var(--gray-100);
}

.conversation-box__threads__footer form {
  align-items: end;
  gap: 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.conversation-box__threads__footer textarea {
  padding: 16px;
  width: 100%;

  border-radius: 4px;
  border: 1px solid var(--gray-200);
  outline: none;
  transition: all 120ms ease-in-out;

  font-size: var(--small-font-size);
  font-family: var(--font-family-sans);
  line-height: var(--small-line-height);
}

.conversation-box__threads__footer textarea:active,
.conversation-box__threads__footer textarea:focus {
  border-color: var(--gray-400);
}

.reply-textarea--disabled {
  pointer-events: none;

  background-color: var(--gray-100);
}

/* NOTE @chris: This is to keep the scroll performant.  */
.conversation-box__bubble-container--scrolling {
  opacity: 0;
  pointer-events: none;
  will-change: opacity;
}
</style>
